export function TrendLineChart({ rows, locationName, annualAverageOverride }) {
  // PIS Step 1 chart layout: x-axis is ISO weeks 1..52/53 (calendar week,
  // not chronological time), prior year drawn as the full annual pattern,
  // current year drawn up to its last completed week, and the annual
  // average shown as a horizontal reference. Two separate lines that share
  // an x-axis indexed by week-of-year — same visual idiom CJ has been
  // reading on pis.empireteaandcoffee.com since the original Apps Script.
  //
  // Wizard payload is newest-first and currently spans iso_year-1.W18 →
  // iso_year.W17 (52 rows). Each row in the current year carries
  // transactions_last_year for the SAME iso_week one year earlier; together
  // with the prior-year rows themselves we can fully reconstruct prior-year
  // weeks 1..52.
  const TREND_CURRENT = '#1565c0';   // current-year (blue)
  const TREND_PRIOR   = '#bdbdbd';   // prior-year line/legend
  const AVG_LINE      = '#ef6c00';   // annual-average reference
  if (!rows || rows.length === 0) {
    return <div style={{ fontSize: 13, color: '#757575' }}>No weekly data to chart.</div>;
  }

  // Identify the current iso year + the latest completed iso week from the
  // payload itself. The newest row's iso_year/iso_week is the last completed
  // week (the in-progress week is intentionally excluded by the upstream).
  const newest = rows[0];
  const currentYear = newest.iso_year;
  const priorYear   = currentYear - 1;
  const currentLatestWeek = newest.iso_week;

  // Build per-iso-week maps for both years.
  const currentByWeek = new Map();
  const priorByWeek   = new Map();
  for (const r of rows) {
    const w = Number(r.iso_week);
    if (!Number.isFinite(w)) continue;
    if (r.iso_year === currentYear) {
      currentByWeek.set(w, Number(r.transactions) || 0);
      // 2026-W3 has transactions_last_year = the 2025-W3 transactions —
      // use that to fill the prior-year half of the year that pre-dates
      // the wizard's window.
      if (r.transactions_last_year != null) {
        priorByWeek.set(w, Number(r.transactions_last_year));
      }
    } else if (r.iso_year === priorYear) {
      priorByWeek.set(w, Number(r.transactions) || 0);
    }
  }

  // Determine max iso_week for the axis. ISO years can have 52 or 53; pick
  // the largest week we've actually seen, floor at 52.
  const maxWeekSeen = Math.max(
    52,
    ...Array.from(currentByWeek.keys()),
    ...Array.from(priorByWeek.keys()),
  );
  const weeks = Array.from({ length: maxWeekSeen }, (_, i) => i + 1);

  // Per-week arrays. Current year capped at currentLatestWeek so the line
  // stops at the last completed week.
  const tx_cur  = weeks.map((w) => (w <= currentLatestWeek && currentByWeek.has(w)) ? currentByWeek.get(w) : null);
  const tx_prev = weeks.map((w) => priorByWeek.has(w) ? priorByWeek.get(w) : null);
  const hasPrev = tx_prev.some((n) => n != null);
  const hasCurrent = tx_cur.some((n) => n != null);

  // Annual-average — prefer server-authoritative; otherwise compute from
  // current-year rows.
  const computedAvg = currentByWeek.size
    ? Math.round(Array.from(currentByWeek.values()).reduce((s, n) => s + n, 0) / currentByWeek.size)
    : null;
  const annualAvg = (annualAverageOverride != null) ? Math.round(annualAverageOverride) : computedAvg;
  const avgIsApprox = annualAverageOverride == null;

  const max = Math.max(
    1,
    ...tx_cur.filter((n) => n != null),
    ...tx_prev.filter((n) => n != null),
    annualAvg || 0,
  );
  function niceMax(v) {
    if (v <= 100) return Math.ceil(v / 50) * 50;
    if (v <= 1000) return Math.ceil(v / 100) * 100;
    if (v <= 5000) return Math.ceil(v / 500) * 500;
    return Math.ceil(v / 1000) * 1000;
  }
  const yMax = niceMax(max);
  const W = 960, H = 300, PL = 44, PR = 16, PT = 16, PB = 32;
  const innerW = W - PL - PR, innerH = H - PT - PB;
  // x maps from week 1..maxWeek across the inner width.
  const xForWeek = (w) => PL + ((w - 1) / (maxWeekSeen - 1)) * innerW;
  const yForVal  = (v) => PT + innerH - (v / yMax) * innerH;
  const pathFor = (arr) => arr
    .map((v, i) => v == null ? null : `${i === 0 || arr[i-1] == null ? 'M' : 'L'} ${xForWeek(weeks[i])} ${yForVal(v)}`)
    .filter(Boolean).join(' ');
  const cur  = pathFor(tx_cur);
  const prev = pathFor(tx_prev);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(yMax * f));
  const avgY = annualAvg != null ? yForVal(annualAvg) : null;
  // x-axis ticks at every 4 weeks plus the last
  const xTickWeeks = weeks.filter((w) => w === 1 || w === maxWeekSeen || w % 4 === 0);

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 300, display: 'block' }}>
        {/* y grid + labels */}
        {yTicks.map((v, i) => {
          const y = yForVal(v);
          return (
            <g key={i}>
              <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#eeeeee" strokeWidth="1" />
              <text x={PL - 6} y={y + 3} fontSize="11" fill="#9e9e9e" textAnchor="end">{v.toLocaleString()}</text>
            </g>
          );
        })}
        {/* annual-average reference line — drawn behind the series so the lines stay clear */}
        {avgY != null && (
          <g>
            <line x1={PL} y1={avgY} x2={W - PR} y2={avgY}
                  stroke={AVG_LINE} strokeWidth="1.4" strokeDasharray="6 4" />
            <text x={W - PR - 4} y={avgY - 4} fontSize="11" fill={AVG_LINE} textAnchor="end" fontWeight="600">
              Annual avg {annualAvg.toLocaleString()} tx/wk{avgIsApprox ? ' (approx)' : ''}
            </text>
          </g>
        )}
        {/* prior-year (full annual pattern) */}
        {hasPrev && prev && (
          <path d={prev} fill="none" stroke={TREND_PRIOR} strokeWidth="2" />
        )}
        {/* current-year (capped at last completed week) */}
        {hasCurrent && cur && (
          <path d={cur} fill="none" stroke={TREND_CURRENT} strokeWidth="2.4" />
        )}
        {/* current-year dots */}
        {tx_cur.map((v, i) => {
          if (v == null) return null;
          return <circle key={`c${weeks[i]}`} cx={xForWeek(weeks[i])} cy={yForVal(v)} r="3" fill={TREND_CURRENT} />;
        })}
        {/* x-axis ticks */}
        {xTickWeeks.map((w) => (
          <text key={w} x={xForWeek(w)} y={H - 8} fontSize="11" fill="#757575" textAnchor="middle">
            W{String(w).padStart(2, '0')}
          </text>
        ))}
      </svg>
      <div style={{ fontSize: 11, color: '#757575', display: 'flex', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
        <span>
          <span style={{ display: 'inline-block', width: 14, height: 2, background: TREND_CURRENT, verticalAlign: 'middle', marginRight: 4 }}/>
          {currentYear} (current YTD, through W{String(currentLatestWeek).padStart(2, '0')})
        </span>
        {hasPrev && (
          <span>
            <span style={{ display: 'inline-block', width: 14, height: 2, background: TREND_PRIOR, verticalAlign: 'middle', marginRight: 4 }}/>
            {priorYear} (prior year — full annual pattern)
          </span>
        )}
        {annualAvg != null && (
          <span>
            <span style={{ display: 'inline-block', width: 14, height: 0, borderTop: `2px dashed ${AVG_LINE}`, verticalAlign: 'middle', marginRight: 4 }}/>
            Annual avg{avgIsApprox ? ' (approx, computed from rendered weeks)' : ''}
          </span>
        )}
      </div>
    </div>
  );
}
